import { LogManager } from '@src/utility/Logger';

import { EditorChangeType } from '../types';

import type { PunchListItem } from '../models/PunchListItem';
import type { TranscriptTextToken, TranscriptToken } from '../types';
import type { VideoEditor } from '../VideoEditor';

const logger = LogManager.getLogger('editor:PunchListTrackManager');

// Epsilon threshold for floating-point comparison
const EPSILON = 0.001;

/**
 * PunchListTrackManager handles:
 * 1. reconciliation of punch list items with transcript changes - reconcilePunchList
 * 2. changes to punch list items - updatePunchList
 */
export class PunchListTrackManager {
  private editor: VideoEditor;

  constructor(editor: VideoEditor) {
    this.editor = editor;
  }

  /**
   * Reconciles punch list items with current transcript state.
   * Called after transcript modifications to ensure image element timings remain in sync.
   */
  public reconcilePunchList(): void {
    const trackManager = this.editor.getTrackManager();
    const punchListItems = trackManager.punchListItems;
    if (punchListItems.size === 0) {
      return; // No punch list items to reconcile
    }

    // Get current transcript tokens
    const tokens = this.editor.getFullTranscript(); // getActiveTranscriptNew
    if (!tokens || tokens.length === 0) {
      return; // No transcript to reconcile against
    }

    // Build a map of global indices to tokens for fast lookup
    const tokenByIndex = this.buildIndexToTokenMap(tokens);

    // Track if any changes were made
    let changesMade = false;

    // Reconcile each punch list item
    for (const [elementId, punchListItem] of punchListItems.entries()) {
      const updated = this.reconcilePunchListItem(
        elementId,
        punchListItem,
        tokenByIndex,
      );
      if (updated) {
        changesMade = true;
      }
    }

    // Notify observers if changes were made to trigger syncFromEditor
    if (changesMade) {
      this.editor.notifyObservers(EditorChangeType.PUNCH_LIST_UPDATED, {
        action: 'reconciled',
      });
    }
  }

  /**
   * Builds a map from initial transcript index to token for fast lookup.
   * Uses initial_index to maintain stable references across transcript changes.
   * Also tracks the current position of each token in the active transcript array.
   */
  private buildIndexToTokenMap(
    tokens: TranscriptToken[],
  ): Map<number, { token: TranscriptToken; currentIndex: number }> {
    const map = new Map<
      number,
      { token: TranscriptToken; currentIndex: number }
    >();

    for (let currentIndex = 0; currentIndex < tokens.length; currentIndex++) {
      const token = tokens[currentIndex];
      if (
        token.initial_index != null &&
        !['removed', 'cut'].includes(token.state || '')
      ) {
        map.set(token.initial_index, { token, currentIndex });
      }
    }

    return map;
  }

  /**
   * Reconciles a single punch list item with current transcript state.
   * Finds the first and last available tokens in the range and updates timing accordingly.
   * If no tokens found in range, removes the punch list item.
   * @returns true if changes were made, false otherwise
   */
  private reconcilePunchListItem(
    elementId: string,
    punchListItem: PunchListItem,
    tokenByIndex: Map<number, { token: TranscriptToken; currentIndex: number }>,
  ): boolean {
    const position = punchListItem.transcriptPosition;

    const startIndex = position.initialStartIndex;
    const endIndex = position.initialEndIndex;

    // Find the first available token in the range (forward scan)
    let firstTokenData: {
      token: TranscriptTextToken;
      currentIndex: number;
    } | null = null;
    for (let i = startIndex; i <= endIndex; i++) {
      const data = tokenByIndex.get(i);
      if (data && data.token.type === 'text') {
        firstTokenData = data as {
          token: TranscriptTextToken;
          currentIndex: number;
        };
        break;
      }
    }

    // Find the last available token in the range (reverse scan)
    let lastTokenData: {
      token: TranscriptTextToken;
      currentIndex: number;
    } | null = null;
    for (let i = endIndex; i >= startIndex; i--) {
      const data = tokenByIndex.get(i);
      if (data && data.token.type === 'text') {
        lastTokenData = data as {
          token: TranscriptTextToken;
          currentIndex: number;
        };
        break;
      }
    }

    // Find the corresponding element on the timeline
    const element = this.editor.getElementById(elementId);
    if (!element) {
      logger.warn('Punch list element not found on timeline', {
        elementId,
        punchListItemId: punchListItem.id,
      });
      return false;
    }

    const newStartTime = firstTokenData?.token.ts;
    const newEndTime = lastTokenData?.token.end_ts;

    // Case 1: No tokens found in range - remove the element
    if (newStartTime == null || newEndTime == null) {
      logger.debug('No tokens found in punch list range, removing element', {
        elementId,
        punchListItemId: punchListItem.id,
        startIndex,
        endIndex,
      });

      // Remove element from timeline
      const trackManager = this.editor.getTrackManager();
      trackManager.removeElement(element);

      // Remove from punch list items Map
      trackManager.punchListItems.delete(elementId);

      return true;
    }
    // Case 2: Tokens found - update timing and current indices
    const newDuration = newEndTime - newStartTime;
    // Only update if timing has changed
    if (
      Math.abs(element.time - newStartTime) > EPSILON ||
      Math.abs(element.durationSeconds - newDuration) > EPSILON
    ) {
      element.update({
        time: newStartTime,
        duration: `${newDuration} s`,
      });

      // Update stored timing in punch list item
      punchListItem.startTime = newStartTime;
      punchListItem.endTime = newEndTime;
      punchListItem.duration = newDuration;

      // Update current indices to reflect the current array positions of the tokens
      if (firstTokenData && lastTokenData) {
        punchListItem.transcriptPosition.startIndex =
          firstTokenData.currentIndex;
        punchListItem.transcriptPosition.endIndex = lastTokenData.currentIndex;
      }

      logger.debug('Reconciled punch list item timing', {
        elementId,
        punchListItemId: punchListItem.id,
        newStartTime,
        newEndTime,
        newDuration,
        newStartIndex: punchListItem.transcriptPosition.startIndex,
        newEndIndex: punchListItem.transcriptPosition.endIndex,
      });

      return true;
    }

    return false;
  }

  /**
   * Recalculates punch list items position (transcript range + line) when
   * timeline elements are modified (shift, trim, etc).
   *
   * @param elementIds - Optional array of element IDs to update. If not provided, updates all.
   */
  public recalculatePunchListPosition(elementIds?: string[]): void {
    const trackManager = this.editor.getTrackManager();
    const punchListItems = trackManager.punchListItems;
    if (punchListItems.size === 0) {
      return;
    }

    // Get full transcript for timestamp lookup
    const transcriptTokens = this.editor.getFullTranscript();
    if (!transcriptTokens || transcriptTokens.length === 0) {
      return;
    }

    // Filter to specified element IDs if provided
    const elementsToUpdate = elementIds
      ? elementIds.filter(id => punchListItems.has(id))
      : Array.from(punchListItems.keys());

    for (const elementId of elementsToUpdate) {
      const punchListItem = punchListItems.get(elementId);
      if (!punchListItem) continue;

      const element = this.editor.getElementById(elementId);
      if (!element) continue;

      const elementStartTime = element.time;
      const elementEndTime = element.time + element.durationSeconds;

      // Find transcript tokens that overlap with element timing
      let newStartIndex = -1;
      let newEndIndex = -1;

      for (let i = 0; i < transcriptTokens.length; i++) {
        const token = transcriptTokens[i];
        if (token.type !== 'text' || !('ts' in token) || !('end_ts' in token)) {
          continue;
        }

        const tokenStartTime = token.ts;
        const tokenEndTime = token.end_ts;

        if (tokenStartTime == null || tokenEndTime == null) {
          continue;
        }

        // Find first token that overlaps with element start
        if (newStartIndex === -1 && tokenEndTime > elementStartTime) {
          newStartIndex = i;
        }

        // Find last token that overlaps with element end
        if (
          tokenStartTime < elementEndTime &&
          tokenEndTime > elementStartTime
        ) {
          newEndIndex = i;
        }
      }

      // Update indices if we found valid tokens
      if (newStartIndex !== -1 && newEndIndex !== -1) {
        punchListItem.transcriptPosition.startIndex = newStartIndex;
        punchListItem.transcriptPosition.endIndex = newEndIndex;
        punchListItem.transcriptPosition.initialStartIndex =
          transcriptTokens[newStartIndex]?.initial_index ?? newStartIndex;
        punchListItem.transcriptPosition.initialEndIndex =
          transcriptTokens[newEndIndex]?.initial_index ?? newEndIndex;

        // Recalculate the line text from the new range
        const rangeTokens = transcriptTokens.slice(
          newStartIndex,
          newEndIndex + 1,
        );
        const newLine = rangeTokens
          .map(token => token.value || '')
          .join('')
          .trim();

        punchListItem.line = newLine;

        logger.debug('Updated punch list item transcript position', {
          elementId,
          punchListItemId: punchListItem.id,
          newStartIndex,
          newEndIndex,
          newLine,
          elementStartTime,
          elementEndTime,
        });
      }
    }

    // Notify observers if any elements were updated to trigger syncFromEditor
    if (elementsToUpdate.length > 0) {
      this.editor.notifyObservers(EditorChangeType.PUNCH_LIST_UPDATED, {
        action: 'position_recalculated',
        elementIds: elementsToUpdate,
      });
    }
  }
}
